"use client";

import { Suspense, useCallback } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import {
  SatelliteNavigationProvider,
  type SatellitePageLoadEvent,
} from "@to/ui";

import { agentDirectoryRouteTemplate } from "@/lib/observability/route-template";

function capturePageLoad(event: SatellitePageLoadEvent) {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  try {
    posthog.capture("satellite_page_load", event);
  } catch {
    // Navigation cannot depend on telemetry delivery.
  }
}

function TelemetryInner({
  tenantKey,
  tenantPathPrefix,
  children,
}: {
  tenantKey: string;
  tenantPathPrefix?: string;
  children?: React.ReactNode;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const routeTemplateForPath = useCallback(
    (path: string) => agentDirectoryRouteTemplate(path, tenantPathPrefix),
    [tenantPathPrefix],
  );

  return (
    <SatelliteNavigationProvider
      application="agentdirectory"
      tenantKey={tenantKey}
      release={process.env.NEXT_PUBLIC_RELEASE_SHA ?? "unknown"}
      currentNavigationKey={`${pathname}?${searchParams.toString()}`}
      routeTemplateForPath={routeTemplateForPath}
      onOutcome={capturePageLoad}
    >
      {children}
    </SatelliteNavigationProvider>
  );
}

export function SatelliteNavigationTelemetry(props: {
  tenantKey: string;
  tenantPathPrefix?: string;
  children?: React.ReactNode;
}) {
  return (
    <Suspense fallback={props.children ?? null}>
      <TelemetryInner {...props} />
    </Suspense>
  );
}
